import React, { FC, useMemo, useState } from 'react';
import { FormikProps } from 'formik';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoCircleFill } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import { ConfirmModal } from '../../../../../components';
import { BracketCellGroup } from '../../../../../utils/groupMatchesByBracketCell';
import {
  getBestOfNWinsNeeded,
  getMatchupProgressionSummary,
  MATCHUP_PROGRESSION_ALL_MATCHES,
  MATCHUP_PROGRESSION_BEST_OF_N,
} from '../../../../../utils/bracketCellMatchupSummary.util';
import {
  applySectionProgressionMode,
  countSectionProgressionChanges,
  implicitSectionProgressionMode,
  inferSectionProgressionMode,
  SECTION_PROGRESSION_MANUAL,
  SECTION_PROGRESSION_MIXED,
} from '../../../../../utils/matchupSectionProgression.util';
import type { PlayValues } from '../PlayTag/PlayTag';

type MatchupSectionToolbarProps = {
  cellGroups: BracketCellGroup[];
  formikArg: FormikProps<PlayValues>;
  variant: 'group' | 'elimination';
  sectionId: string;
  fallbackRubberCount?: number;
};

type PendingApply = {
  mode: number;
  changeCount: number;
  scoredCount: number;
};

export const MatchupSectionToolbar: FC<MatchupSectionToolbarProps> = ({
  cellGroups,
  formikArg,
  variant,
  sectionId,
  fallbackRubberCount = 0,
}) => {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const cells = useMemo(() => cellGroups.filter((group) => !group.isLegacySingle), [cellGroups]);
  const implicitDefault = implicitSectionProgressionMode(variant);
  const inferred = inferSectionProgressionMode(
    cells,
    formikArg.values.bracketCells,
    implicitDefault
  );
  const sampleMatches = cells[0]?.matches ?? [];
  const summary = getMatchupProgressionSummary(sampleMatches, MATCHUP_PROGRESSION_BEST_OF_N);
  const bestOfN = summary.bestOfN > 0 ? summary.bestOfN : fallbackRubberCount;
  const winsNeeded =
    summary.bestOfN > 0 ? summary.winsNeeded : getBestOfNWinsNeeded(fallbackRubberCount);
  const [pending, setPending] = useState<PendingApply | null>(null);

  const selectValue =
    pending != null
      ? String(pending.mode)
      : inferred === SECTION_PROGRESSION_MIXED
      ? SECTION_PROGRESSION_MIXED
      : inferred === SECTION_PROGRESSION_MANUAL
      ? SECTION_PROGRESSION_MANUAL
      : String(inferred);

  const shownMode = pending?.mode ?? inferred;
  const showHint =
    shownMode !== SECTION_PROGRESSION_MIXED && shownMode !== SECTION_PROGRESSION_MANUAL;
  const hintKey =
    shownMode === MATCHUP_PROGRESSION_BEST_OF_N
      ? 'bracketCellProgressionBestOfNHint'
      : 'bracketCellProgressionAllMatchesHint';

  const modeLabel = (mode: number) =>
    mode === MATCHUP_PROGRESSION_BEST_OF_N
      ? t(prefix + 'bracketCellProgressionBestOfN', { n: bestOfN })
      : t(prefix + 'bracketCellProgressionAllMatches');

  const applyPending = () => {
    if (!pending) {
      return;
    }
    applySectionProgressionMode(
      formikArg.setFieldValue,
      cells.map((cell) => cell.cellId),
      pending.mode
    );
    setPending(null);
  };

  const clearPending = () => setPending(null);

  return (
    <div
      className="d-flex flex-wrap align-items-center gap-2"
      data-testid="matchup-section-toolbar"
    >
      <label className="form-label small mb-0" htmlFor={`section-progression-${sectionId}`}>
        {t(prefix + 'bracketCellSectionWinBy')}
      </label>
      <select
        id={`section-progression-${sectionId}`}
        className="form-select form-select-sm"
        style={{ width: 'auto', minWidth: '10rem' }}
        value={selectValue}
        aria-describedby={showHint ? `section-progression-hint-${sectionId}` : undefined}
        onChange={(e) => {
          const value = e.target.value;
          if (value === SECTION_PROGRESSION_MIXED || value === SECTION_PROGRESSION_MANUAL) {
            return;
          }
          const nextMode = Number(value);
          if (inferred === nextMode) {
            return;
          }
          const counts = countSectionProgressionChanges(
            cells,
            formikArg.values.bracketCells,
            nextMode,
            implicitDefault
          );
          if (counts.changeCount === 0) {
            applySectionProgressionMode(
              formikArg.setFieldValue,
              cells.map((cell) => cell.cellId),
              nextMode
            );
            return;
          }
          setPending({ mode: nextMode, ...counts });
        }}
      >
        {inferred === SECTION_PROGRESSION_MIXED ? (
          <option value={SECTION_PROGRESSION_MIXED} disabled>
            {t(prefix + 'bracketCellSectionMixed')}
          </option>
        ) : null}
        {inferred === SECTION_PROGRESSION_MANUAL ? (
          <option value={SECTION_PROGRESSION_MANUAL} disabled>
            {t(prefix + 'bracketCellProgressionManual')}
          </option>
        ) : null}
        <option value={MATCHUP_PROGRESSION_ALL_MATCHES}>
          {t(prefix + 'bracketCellProgressionAllMatches')}
        </option>
        <option value={MATCHUP_PROGRESSION_BEST_OF_N}>
          {t(prefix + 'bracketCellProgressionBestOfN', { n: bestOfN })}
        </option>
      </select>
      {showHint ? (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`section-progression-hint-${sectionId}`}>
              {t(prefix + hintKey, { winsNeeded })}
            </Tooltip>
          }
        >
          <span
            className="d-inline-flex"
            role="img"
            aria-label={t(prefix + hintKey, { winsNeeded })}
          >
            <InfoCircleFill className="text-muted" size={14} aria-hidden />
          </span>
        </OverlayTrigger>
      ) : null}
      <ConfirmModal
        opened={pending != null}
        onConfirm={applyPending}
        onCancel={clearPending}
        onHide={clearPending}
        text={
          pending
            ? t('confirm-applyMatchupWinMode', {
                count: pending.changeCount,
                mode: modeLabel(pending.mode),
              })
            : undefined
        }
        description={
          pending && pending.scoredCount > 0
            ? t('confirm-applyMatchupWinModeScored', { scored: pending.scoredCount })
            : undefined
        }
      />
    </div>
  );
};
